import { attachmentContentType, isImageAttachment } from "./copilotImageQuality"

/**
 * LAB-103: what a chat message remembers about a file it carried. Only metadata -
 * the file itself is sent to Copilot Studio and never stored by LabWizard, so a
 * reopened chat shows the card but not the image preview.
 */
export type CopilotAttachmentMeta = {
  name: string
  contentType: string
  size?: number
}

export type CopilotAttachmentKind =
  | "image"
  | "pdf"
  | "text"
  | "document"
  | "file"

export function attachmentMetaFromFile(file: File): CopilotAttachmentMeta {
  return {
    name: file.name,
    contentType: attachmentContentType(file),
    size: file.size,
  }
}

function extensionOf(name: string) {
  const match = /\.([a-z0-9]{1,8})$/i.exec(name)
  return match ? match[1].toLowerCase() : ""
}

export function attachmentKind(
  attachment: Pick<CopilotAttachmentMeta, "name" | "contentType">
): CopilotAttachmentKind {
  const type = (attachment.contentType || "").toLowerCase()
  const extension = extensionOf(attachment.name)
  if (isImageAttachment({ name: attachment.name, type })) return "image"
  if (type === "application/pdf" || extension === "pdf") return "pdf"
  if (type.startsWith("text/") || ["txt", "csv", "md"].includes(extension)) {
    return "text"
  }
  if (["doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(extension)) {
    return "document"
  }
  return "file"
}

/** Short type badge shown on the card, e.g. "PDF", "TXT", "PNG". */
export function attachmentTypeLabel(
  attachment: Pick<CopilotAttachmentMeta, "name" | "contentType">
) {
  const extension = extensionOf(attachment.name)
  if (extension) return extension.toUpperCase()
  const subtype = (attachment.contentType || "").split("/")[1] || ""
  const last = subtype.split(/[.+-]/).pop()
  return last ? last.toUpperCase() : "FILE"
}

export function formatAttachmentSize(size?: number) {
  if (size == null || !Number.isFinite(size) || size < 0) return ""
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function firstImageAttachment(attachments?: CopilotAttachmentMeta[]) {
  return (attachments || []).find(
    (attachment) => attachmentKind(attachment) === "image"
  )
}

/**
 * When a file is sent, the saved user text ends with an "Attached <file name>"
 * line (LAB-99 reads it back to find a PDF in a reopened chat). Chats saved
 * before LAB-103 have only that line and no metadata, so recover the card from it.
 */
const ATTACHED_LINE = /^Attached\s+(\S(?:.*\S)?\.[a-z0-9]{1,8})$/i

export function attachmentsFromText(text: string): CopilotAttachmentMeta[] {
  const lastLine = text.trim().split("\n").pop() || ""
  const match = ATTACHED_LINE.exec(lastLine.trim())
  if (!match) return []
  const name = match[1]
  return [{ name, contentType: attachmentContentType({ name }) }]
}

/** The card shows the file, so a user bubble drops its "Attached <file name>" line. */
export function userMessageDisplayText(
  text: string,
  attachments: CopilotAttachmentMeta[]
) {
  const placeholders = new Set(
    attachments.map((attachment) => `attached ${attachment.name}`.toLowerCase())
  )
  return text
    .split("\n")
    .filter((line) => !placeholders.has(line.trim().toLowerCase()))
    .join("\n")
    .trim()
}
